import { z } from 'zod';
import { platformModuleIdSchema } from './platform-module-manifest.contract.js';
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const semanticVersionSchema = z.string().regex(/^[0-9]+\.[0-9]+\.[0-9]+$/);
const uniqueConfigurationSchemas = z.array(z.strictObject({
    key: z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+$/),
    schemaVersion: z.number().int().positive(),
})).superRefine((schemas, context) => {
    const seen = new Set();
    for (const [index, schema] of schemas.entries()) {
        if (seen.has(schema.key)) {
            context.addIssue({
                code: 'custom',
                message: `Duplicate configuration schema key: ${schema.key}`,
                path: [index, 'key'],
            });
        }
        seen.add(schema.key);
    }
});
export const platformModuleArchiveRequestSchema = z.strictObject({
    moduleId: platformModuleIdSchema,
    configurationReleaseId: z.string().uuid().nullable(),
    configurationSchemas: uniqueConfigurationSchemas,
    retainUntil: z.string().datetime({ offset: true }).nullable(),
    reason: z.string().trim().min(10).max(1_000),
});
export const platformModuleArchiveTombstoneSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    moduleId: platformModuleIdSchema,
    moduleVersion: semanticVersionSchema,
    archiveSequence: z.number().int().positive(),
    activationEpoch: z.number().int().nonnegative(),
    releaseCatalogHash: sha256Schema,
    manifestHash: sha256Schema,
    migrationSetHash: sha256Schema,
    configurationSchemaHash: sha256Schema,
    configurationReleaseId: z.string().uuid().nullable(),
    migrationIds: z.array(z.string()),
    settingKeys: z.array(z.string()),
    moduleOwner: platformModuleIdSchema,
    retentionPolicy: z.string().min(1).max(500),
    retainUntil: z.string().datetime({ offset: true }).nullable(),
    reason: z.string().min(10).max(1_000),
    archivedByMembershipId: z.string().uuid(),
    archivedAt: z.string().datetime({ offset: true }),
});
export const platformModuleReclaimRequestSchema = z.strictObject({
    tombstoneId: z.string().uuid(),
    compatibilityMode: z.enum(['exact-manifest', 'schema-compatible']),
    configurationSchemas: uniqueConfigurationSchemas,
    healthEvidenceHash: sha256Schema,
    reconciliationEvidenceHash: sha256Schema.nullable(),
});
export const platformModuleReclaimEvidenceSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    tombstoneId: z.string().uuid(),
    moduleId: platformModuleIdSchema,
    compatibilityMode: z.enum(['exact-manifest', 'schema-compatible']),
    targetModuleVersion: semanticVersionSchema,
    targetReleaseCatalogHash: sha256Schema,
    targetManifestHash: sha256Schema,
    targetMigrationSetHash: sha256Schema,
    targetConfigurationSchemaHash: sha256Schema,
    healthEvidenceHash: sha256Schema,
    reconciliationEvidenceHash: sha256Schema.nullable(),
    reclaimedByMembershipId: z.string().uuid(),
    reclaimedAt: z.string().datetime({ offset: true }),
});
export const platformModuleDestructionRequestSchema = z.strictObject({
    tombstoneId: z.string().uuid(),
    backupEvidenceHash: sha256Schema,
    reason: z.string().trim().min(20).max(2_000),
});
export const platformModuleDestructionRequestRecordSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    tombstoneId: z.string().uuid(),
    moduleId: platformModuleIdSchema,
    backupEvidenceHash: sha256Schema,
    reason: z.string().min(20).max(2_000),
    requestedByMembershipId: z.string().uuid(),
    requestedAt: z.string().datetime({ offset: true }),
    status: z.literal('awaiting-separate-authorization'),
});
//# sourceMappingURL=platform-module-archive.contract.js.map